import { AdminDataQuery, FEDERAL_STATES, FederalState } from "@autfit/shared-types";

export const ADMIN_DATA_PAGE_SIZES = [25, 50, 100, 200];
export const ADMIN_DATA_DEFAULT_PAGE_SIZE = 50;

const LIST_KEYS = [
  "zipCodes",
  "federalStates",
  "organizationIds",
  "interventionIds",
  "classNames",
] as const;

const toNumber = (value: string | null): number | undefined => {
  if (value === null || value.trim() === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

/** URL query string -> filter/paging state. Invalid values are dropped. */
export function parseAdminDataSearch(params: URLSearchParams): AdminDataQuery {
  const query: AdminDataQuery = {};
  const page = toNumber(params.get("page"));
  if (page && page >= 1) query.page = Math.floor(page);
  const pageSize = toNumber(params.get("pageSize"));
  if (pageSize && ADMIN_DATA_PAGE_SIZES.includes(pageSize)) {
    query.pageSize = pageSize;
  }

  const dateFrom = params.get("dateFrom");
  if (dateFrom) query.dateFrom = dateFrom;
  const dateTo = params.get("dateTo");
  if (dateTo) query.dateTo = dateTo;

  for (const key of LIST_KEYS) {
    const values = params.getAll(key).filter((v) => v.trim() !== "");
    if (values.length) (query as Record<string, unknown>)[key] = values;
  }
  if (query.federalStates) {
    query.federalStates = query.federalStates.filter((s) =>
      FEDERAL_STATES.includes(s as FederalState),
    );
    if (!query.federalStates.length) delete query.federalStates;
  }

  const ageMin = toNumber(params.get("ageMin"));
  if (ageMin !== undefined) query.ageMin = ageMin;
  const ageMax = toNumber(params.get("ageMax"));
  if (ageMax !== undefined) query.ageMax = ageMax;

  if (params.get("withoutIntervention") === "true") {
    query.withoutIntervention = true;
  }
  const gender = params.get("gender");
  if (gender === "MALE" || gender === "FEMALE") query.gender = gender;
  return query;
}

/**
 * Filter/paging state -> query string (arrays as repeated params, empty values
 * omitted). Used both for the page URL and for the API requests.
 */
export function toAdminDataSearch(query: AdminDataQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.page && query.page > 1) params.set("page", String(query.page));
  if (query.pageSize) params.set("pageSize", String(query.pageSize));
  if (query.dateFrom) params.set("dateFrom", query.dateFrom);
  if (query.dateTo) params.set("dateTo", query.dateTo);
  for (const key of LIST_KEYS) {
    for (const value of query[key] ?? []) params.append(key, value);
  }
  if (query.ageMin !== undefined) params.set("ageMin", String(query.ageMin));
  if (query.ageMax !== undefined) params.set("ageMax", String(query.ageMax));
  if (query.withoutIntervention) params.set("withoutIntervention", "true");
  if (query.gender) params.set("gender", query.gender);
  return params;
}

/** Filters only (drops paging), e.g. for the export. */
export function withoutPaging(query: AdminDataQuery): AdminDataQuery {
  const { page, pageSize, ...filters } = query;
  return filters;
}

/** Splits typed PLZ input like "1010, 1020;8010 9020" into distinct entries. */
export function parseZipInput(text: string): string[] {
  return Array.from(
    new Set(
      text
        .split(/[\s,;]+/)
        .map((z) => z.trim())
        .filter((z) => z.length > 0),
    ),
  );
}

/** First 8 characters of an id, e.g. for a compact table cell. */
export function shortenId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}…` : id;
}
